// src/components/chat/ScrollToBottomButton.tsx
import React from 'react';
import { Fab, Zoom, Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { styled } from '@mui/material/styles';

interface ScrollToBottomButtonProps {
  /**
   * Whether to show the button
   */
  show: boolean;
  /**
   * Click handler
   */
  onClick: () => void;
}

const ButtonContainer = styled(Box)(({ theme }) => ({
  position: 'sticky',
  bottom: '24px', // Very close to the chat input for minimal obstruction
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  pointerEvents: 'none', // Allow clicks to pass through the container
  zIndex: 10,
  marginTop: '-60px', // Negative margin to overlay above content
  marginBottom: '0',
  height: 0, // Don't take up space in the layout
  [theme.breakpoints.down('sm')]: {
    bottom: '20px',
  }
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  pointerEvents: 'auto', // Re-enable clicks on the button itself
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
    transform: 'translateY(-2px)', // Subtle lift on hover
  },
  transition: 'all 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    width: 40,
    height: 40,
    minHeight: 40,
  }
}));

/**
 * Floating button that appears when user scrolls up
 * Scrolls to bottom on click
 * Positioned elegantly in the center, just above the chat input (ChatGPT-style)
 */
export const ScrollToBottomButton: React.FC<ScrollToBottomButtonProps> = ({
  show,
  onClick
}) => {
  return (
    <ButtonContainer>
      <Zoom in={show} timeout={300} unmountOnExit>
        <StyledFab
          size="medium"
          onClick={onClick}
          aria-label="Scroll to bottom"
        >
          <KeyboardArrowDownIcon />
        </StyledFab>
      </Zoom>
    </ButtonContainer>
  );
};

export default ScrollToBottomButton;

